import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Container, Grid, useMediaQuery } from "@mui/material";
import { gsap } from "gsap";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import { aboutus1, aboutus2, aboutus3, backsidebackgrounddesktop, backsidebackgroundmobile, gardientline, line } from "../../../assets";

const AboutUs = () => {
  const textRef = useRef(null);
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isHoveredRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  // Image rotation effect
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isPaused) {
        setActiveIndex((prev) => (prev + 1) % 3);
      }
    }, 5000); // Rotate every 5 seconds

    return () => clearInterval(interval);
  }, [isPaused]);

  useEffect(() => {
    if (textRef.current) {
      gsap.fromTo(
        textRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1, ease: "power2.out" }
      );
    }
  }, []);

  const images = [
    "https://dprstorage.b-cdn.net/kolla/Luxuria/GateFinal.jpg",
    "https://dprstorage.b-cdn.net/kolla/Luxuria/WhatsApp%20Image%202025-03-15%20at%2002.54.01%20(2).jpeg",
    "https://dprstorage.b-cdn.net/kolla/Luxuria/WhatsApp%20Image%202025-03-15%20at%2002.54.01%20(1).jpeg",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isPaused && !isHoveredRef.current) {
        setActiveIndex((prev) => (prev + 1) % images.length);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused]);

  useEffect(() => {
    gsap.fromTo(
      ".image-box",
      { opacity: 0, scale: 0.8 },
      { opacity: 1, scale: 1, duration: 0.8, ease: "power2.out" }
    );
  }, [activeIndex]);

  const handleMouseEnter = (index: number) => {
    isHoveredRef.current = true;
    clearTimeout(hoverTimeoutRef.current!);
    setIsPaused(true);
    setActiveIndex(index);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      isHoveredRef.current = false;
      setIsPaused(false);
    }, 500);
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "auto",
        backgroundImage: isMobile ? `url(${backsidebackgroundmobile})` : `url(${backsidebackgrounddesktop})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        px: { xs: 1, md: 2 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          color: "white",
          py: { xs: 1, md: 4 },
          px: { xs: 1, md: 2 },
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ textAlign: "left", mb: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: "600",
                fontSize: "28px",
                background: "linear-gradient(90deg, #bf742c, #fff8da, #c58038)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent", 
                display: "inline-block",
              }}
            >
              About Us
                </Typography>
                  <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", my: 1, width: "100%" }}>
                    <img src={gardientline} alt="Best apartments in Hyderabad by Kolla Group" style={{ width: "100%", maxWidth: "900px", marginLeft: 0 }} />
                  </Box>
                </Box>

          <Box sx={{ textAlign: "left", mb: 0 }}>
            <span
              // className="aboutus-description"
              style={{ fontSize: "18px", maxWidth: "600px", marginBottom: "20px", textAlign: "justify", fontWeight: "300", fontFamily: "Bebas Neue", letterSpacing: "0.8px", color: 'rgba(180, 180, 180, 0.8)' }}
              ref={textRef}
            >
              Experience the pinnacle of luxury living at Kolla Luxuria, an exclusive 12-acre enclave where elegance meets comfort in every corner. Designed for the discerning few, this project redefines sophistication with ultra-premium villas and limited-edition residences.
            </span>
          </Box>

          <Box
             sx={{
               display: "flex",
               justifyContent: "center",
               alignItems: "center",
               position: "relative",
               height: { xs: 400, md: 500 },
               mb: 1,
               overflow: "visible",
             }}
           >
             {images.map((src, index) => {
               let transform = "scale(0.85)";
               let zIndex = 1;
               let opacity = 0.7;
               let width = { xs: "60%", md: "30%" };
       
               if (index === (activeIndex + 2) % images.length) {
                 // Left Image
                 transform = "translateX(-40%) rotate(-15deg) scale(0.8)";
                 zIndex = 1;
               } else if (index === activeIndex) {
                 // Center Image (Active)
                 transform = "scale(1)";
                 zIndex = 3;
                 opacity = 1;
                 width = { xs: "80%", md: "40%" };
               } else if (index === (activeIndex + 1) % images.length) {
                 // Right Image
                 transform = "translateX(40%) rotate(15deg) scale(0.8)";
                 zIndex = 1;
               }
       
               return (
                 <Box
                   key={index}
                   component="img"
                   src={src}
                   alt={`Image ${index + 1}`}
                   className="image-box"
                   sx={{
                     width,
                     position: "absolute",
                     transition: "all 0.7s ease-in-out",
                     border: "3px solid #D4AF37",
                     backgroundColor: "#fff",
                     p: 1,
                     cursor: "pointer",
                     zIndex,
                     transform,
                     opacity,
                     boxShadow: zIndex === 3 ? "0px 15px 50px rgba(0,0,0,0.5)" : "none",
                   }}
                   onMouseEnter={() => handleMouseEnter(index)}
                   onMouseLeave={handleMouseLeave}
                   onClick={() => {
                     setPhotoIndex(index);
                     setIsOpen(true);
                   }}
                 />
               );
             })}
           </Box>

          {isOpen && (
            <Lightbox
              mainSrc={images[photoIndex]}
              nextSrc={images[(photoIndex + 1) % images.length]}
              prevSrc={images[(photoIndex + images.length - 1) % images.length]}
              onCloseRequest={() => setIsOpen(false)}
              onMovePrevRequest={() =>
                setPhotoIndex((photoIndex + images.length - 1) % images.length)
              }
              onMoveNextRequest={() =>
                setPhotoIndex((photoIndex + 1) % images.length)
              }
            />
          )}

          {/* Mobile Indicator */}
          {isMobile && (
            <Box 
              sx={{ 
                display: "flex", 
                justifyContent: "center", 
                gap: 1,
                mb: 2 
              }}
            >
              {/* {[0, 1, 2].map((index) => (
                <Box
                  key={index}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: activeIndex === index ? "#D4AF37" : "rgba(212, 175, 55, 0.3)",
                    transition: "all 0.3s ease",
                  }}
                />
              ))} */}
            </Box>
          )}
          {!isMobile && (
            <Grid container spacing={3} sx={{ textAlign: "center", position: "relative" }}>
              {[{ label: "350 - 500 Sq. Ft. Yds.", value: "69 Villas"  }, { label: "4,200 Sq. Ft", value: "5 Condominiums" }, { label: "of Exclusively Curated Luxury", value: "12 Acres" },  ].map((item, index, array) => (
                <Grid
                  item
                  xs={6}
                  md={4}
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: "600",
                        fontSize: "36px",
                        background: "linear-gradient(90deg, rgb(201, 140, 83), rgb(226, 205, 186), rgb(201, 138, 71))",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        display: "inline-block",
                      }}
                    >
                      {item.value}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "12px", opacity: 0.7, fontWeight: 300, letterSpacing: 1 , fontFamily: "LEMON MILK", color: 'rgba(235, 235, 235, 0.9)' }}>
                    {item.label}
                  </Typography>
                  </Box>

                  {/* Vertical Line Separator (Right Side) */}
                  {index !== array.length - 1 && (
                    <Box
                      sx={{
                        position: "absolute",
                        right: 0, // Positioned at the right side
                        top: "50%",
                        transform: "translateX(50%) translateY(-50%)",
                        height: "80px",
                        width: "2px",
                        mt: 2,
                        backgroundColor: "rgba(201, 140, 83, 0.7)",
                      }}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
          )}

          {/* Mobile View Separator */}
          {isMobile && (
            <Grid container spacing={3} sx={{ textAlign: "center", position: "relative", mt: "-60px" }}>
              {[{ label: "350 - 500 Sq. Ft. Yds.", value: "69 Villas"  }, { label: "4,200 Sq. Ft", value: "5 Condominiums" }, { label: "of Exclusively Curated Luxury", value: "12 Acres" },  ].map((item, index, array) => (
                <Grid item xs={12} key={index} sx={{ position: "relative" }}>
                  <Box sx={{ pb: 2, mb: 2 }}>
                  <Typography 
                    variant="h2" 
                    sx={{
                    fontWeight: "600",
                    fontSize: "34px",
                    background: "linear-gradient(90deg,rgb(201, 140, 83), rgb(226, 205, 186) ,rgb(201, 138, 71))",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent", 
                    display: "inline-block",
                    }}
                  >
                    {item.value}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "10px", opacity: 0.7, fontWeight: 300, letterSpacing: 1 , fontFamily: "LEMON MILK", color: 'rgba(235, 235, 235, 0.9)' }}>
                    {item.label}
                  </Typography>
                  </Box>
                  {/* Vertical Line Separator */}
                  {/* {index % 2 === 0 && index !== array.length - 1 && (
                  <Box 
                    sx={{
                    position: "absolute",
                    right: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    height: "55px",
                    width: "1px",
                    background: "linear-gradient( #bf742c, #fff8da, #c58038)",
                    opacity: 0.5,
                    }}
                  />
                  )} */}
                  {/* Horizontal Line Separator */}
                  {/* {index % 2 !== 0 && index !== array.length - 1 && (
                  <Box 
                    sx={{
                    position: "absolute",
                    bottom: 0,
                    left: "0%",
                    transform: "translateX(-25%)",
                    height: "1px",
                    width: "500px",
                    background: "linear-gradient(90deg, #bf742c, #fff8da, #c58038)",
                    opacity: 0.5,
                    }}
                  />
                  )} */}
                </Grid>
              ))}
            </Grid>
          )}

          {/* <Box sx={{ textAlign: "center", mt: 2 }}>
            <span
              style={{
                textAlign: "center",
                marginTop: 4,
                fontFamily: "Bebas Neue", 
                fontSize: "18px",
                letterSpacing: 1,
                fontWeight: '200',
                marginRight: "10px",
                color: 'rgba(180, 180, 180, 0.8)' 
              }}
            >
              WITH <span style={{ color: 'rgba(221, 164, 57, 0.82)' , fontFamily: "Bebas Neue", fontSize: "18px", fontWeight: "500" }}> RERA APPROVAL</span>,<br /> 
            </span>
            <span
              style={{
                textAlign: "center",
                marginTop: 1,
                fontFamily: "Bebas Neue",
                fontWeight: "500",
                fontSize: "18px",
                letterSpacing: 1,color: 'rgba(180, 180, 180, 0.8)' 
              }}
            >
              IT STANDS AS AN ENDURING SYMBOL OF PRESTIGE & SMART INVESTMENT.
            </span>
          </Box> */}
        </Container>
      </Box>
    </Box>
  );
};

export default AboutUs;
